import { Link, useNavigate } from "react-router-dom";
import ContentLayout from "../components/layout/ContentLayout";
import { CiCirclePlus } from "react-icons/ci";
import OutletCard from "../components/OutletCard";
import { useSelector } from "react-redux";
import {
  addStampCode,
  deleteOutlet,
  getOutlets,
  deleteStampCode,
} from "../actions/acc_actions";
import { useQuery } from "@tanstack/react-query";
import Loading from "../utils/Loading";
import MyModal from "../components/MyModal";
import { Button, Input, message, Modal } from "antd";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Outlets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const modalRef = useRef();
  const businessUrn = useSelector((state) => state.account.businessUrn);

  const { refetch, isLoading, data } = useQuery({
    queryKey: ["outlets", businessUrn],
    queryFn: () => getOutlets(businessUrn),
    enabled: !!businessUrn,
  });

  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [stampCode, setStampCode] = useState("");

  if (isLoading) return <Loading />;

  const outlets = data?.data?.body;
  const isEmpty = outlets?.length === 0;

  const handleAddStampCode = () => {
    if (selectedOutlet) {
      addStampCode(selectedOutlet.urn, businessUrn, { stampCode })
        .then(() => {
          refetch();
          modalRef.current.hideModal();
          message.success(t("stamp_code_added"));
        })
        .catch(() => message.error(t("stamp_code_delete_failed")));
    }
  };

  const handleAddStamp = (outlet) => {
    setSelectedOutlet(outlet);
    modalRef.current?.showModal();
  };

  const handleDelete = (outletUrn) => {
    Modal.confirm({
      title: t("confirm_delete_outlet"),
      content: t("confirm_delete_outlet_message"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        return deleteOutlet({ businessUrn, outletUrn })
          .then(() => {
            refetch();
            message.success(t("outlet_deleted_success"));
          })
          .catch(() => message.error(t("outlet_delete_failed")));
      },
    });
  };

  const handleDeleteStampCode = (outletUrn, code) => {
    Modal.confirm({
      title: t("confirm_delete_stamp_code"),
      content: t("confirm_delete_stamp_code_message", { stampCode: code }),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: async () => {
        return deleteStampCode({ businessUrn, outletUrn, stampCode: code })
          .then(() => {
            refetch();
            modalRef.current.hideModal();
            message.success(t("stamp_code_added"));
          })
          .catch(() => message.error(t("stamp_code_delete_failed")));
      },
    });
  };

  return (
    <ContentLayout
      header={
        isEmpty ? (
          <div className="screen-notification">
            <img src="/assets/images/bg/outlets.png" alt="" />
            <h2 className="active">{t("start_adding_outlets")}</h2>
            <h5>{t("add_outlets_description")}</h5>
            <Link className="btn primary" to="add">
              {t("add_first_outlet")}
            </Link>
          </div>
        ) : (
          <>
            <h1>{t("welcome_outlets")}</h1>
            <div>
              <h3 className="content_layout-subtitle">
                {t("add_your_outlets")}
              </h3>
              <h6 className="content_layout-subtitle">
                {t("additional_outlet_cost")}
              </h6>
            </div>
          </>
        )
      }
    >
      {!isEmpty && (
        <div className="outlet-cards">
          <Link to="add" className="outlet-add btn">
            <CiCirclePlus size={48} />
            <div className="outlet-add-content">
              <p>{t("add_new")}</p>
              <h4 className="active">{t("outlet")}</h4>
            </div>
          </Link>

          {outlets?.map((item) => (
            <OutletCard
              outlet={item}
              key={item.urn}
              businessName={item.name}
              stampCodes={item.stamp_codes}
              address={item.address}
              created={item.created}
              approveStatus={item.approve_status}
              onAddStampDevice={() => handleAddStamp(item)}
              onDelete={() => handleDelete(item.urn)}
              showAddStampDevice={true}
              onEdit={() => navigate(`/outlets/edit/${item?.urn}`)}
            />
          ))}
        </div>
      )}

      <MyModal
        ref={modalRef}
        title={t("add_stamp_code")}
        message={t("add_stamp_device_message")}
        imageSrc="/assets/images/modal/stamp.png"
        additionalContent={
          <>
            <Input
              value={stampCode}
              onChange={(e) => setStampCode(e.target.value)}
              placeholder={t("enter_stamp_code")}
            />
            <Button
              style={{ width: "100%", marginBottom: "16px" }}
              type="primary"
              onClick={handleAddStampCode}
            >
              {t("add")}
            </Button>
            {selectedOutlet?.stamp_codes?.length > 0 && (
              <div className="list">
                <h6>{t("stamp_codes")}</h6>
                {selectedOutlet.stamp_codes.map((code) => (
                  <div className="row" key={code}>
                    <h5 className="stamp-code">{code}</h5>
                    <Button
                      type="default"
                      danger
                      onClick={() =>
                        handleDeleteStampCode(selectedOutlet.urn, code)
                      }
                    >
                      {t("delete")}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        }
      />
    </ContentLayout>
  );
}
